import React from 'react';
import useAudioServerCommunicationContext from '../AudioServerCommunicationContextProvider/useAudioServerCommunicationContext';
import useSessionContextContext from '../SessionContextContextProvider/useSessionContextContext';
import useSessionInfoContext from '../SessionInfoContextProvider/useSessionInfoContext';
import useSessionLanguageContext from '../SessionLanguageContextProvider/useSessionLanguageContext';
import useAudioWebsocketApiConnectionContext from '../WebsocketConnectionProviders/AudioWebsocketApiConnectionContext/useAudioWebsocketApiConnectionContext';
import useSpeechRecognition from './Hooks/useSpeechRecognition';
import { SessionConnectionContext } from './useSessionConnectionContext';

export default function SessionConnectionContextProvider({ children }: { children: React.ReactNode }) {
  const {
    session, endCause, setEndingSession,
  } = useSessionInfoContext();
  const { sendJsonMessage: sendToAudioWebsocket } = useAudioServerCommunicationContext();

  const { context } = useSessionContextContext();
  const { sessionLanguage } = useSessionLanguageContext();
  const { sendJsonMessage, lastJsonMessage } = useAudioWebsocketApiConnectionContext();

  const lastTranscript = useSpeechRecognition(sessionLanguage);

  const sessionIdRef = React.useRef<string | null>(session?.id ?? null);
  React.useEffect(() => {
    sessionIdRef.current = session?.id ?? null;
  }, [session?.id]);

  React.useEffect(() => {
    if (lastTranscript) {
      sendJsonMessage({
        ...lastTranscript,
        date: Date.now() * 1e6,
      });
    }
  }, [lastTranscript]);

  React.useEffect(() => {
    if (sessionIdRef.current) {
      sendJsonMessage({
        type: 'settings',
        context: context ?? '',
        date: Date.now() * 1e6,
      });
    }
  }, [context, sessionIdRef]);

  React.useEffect(() => {
    if (endCause) {
      sendToAudioWebsocket({
        type: 'endSession',
        cause: endCause,
        date: Date.now() * 1e6,
      });

      switch (endCause) {
        case 'ProjectChanged':
        case 'LanguageChanged':
          // If the reason for terminating a session is that we are creating another session,
          // no need for any logic to ensure we are properly terminating the session.
          // Whatever happens, we will already be in another session.
          break;

        default:
          setEndingSession(sessionIdRef.current);
      }
    }
  }, [endCause, sessionIdRef]);

  const value = React.useMemo(() => ({
    latestMessage: lastJsonMessage,
  }), [lastJsonMessage]);

  return (
    <SessionConnectionContext.Provider value={value}>
      {children}
    </SessionConnectionContext.Provider>
  );
}
